import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Palette, RefreshCw, Type, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function BrandIdentity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [style, setStyle] = useState("modern");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/brand", { style });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load brand");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const regen = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/brand/regenerate", { style });
      setData(data);
      toast.success("Brand identity regenerated");
    } catch (e) {
      toast.error("Could not regenerate");
    } finally {
      setGenerating(false);
    }
  };

  if (loading)
    return (
      <div className="space-y-4">
        <div className="h-10 w-72 bg-white/5 rounded animate-pulse" />
        <div className="grid grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-32 bg-white/5 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );

  const b = data?.brand || {};

  return (
    <div className="space-y-8" data-testid="brand-root">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Brand Identity</div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">{b.tagline || "Your brand"}</h1>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={style}
            data-testid="brand-style-select"
            onChange={(e) => setStyle(e.target.value)}
            className="bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-2.5 text-sm"
          >
            <option value="modern">Modern</option>
            <option value="luxury">Luxury</option>
            <option value="minimal">Minimal</option>
            <option value="playful">Playful</option>
            <option value="bold">Bold</option>
            <option value="elegant">Elegant</option>
          </select>
          <button
            onClick={regen}
            data-testid="brand-regenerate"
            disabled={generating}
            className="inline-flex items-center gap-2 brand-gradient px-4 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />
            Regenerate
          </button>
        </div>
      </header>

      {/* Mission/Vision/Values */}
      <div className="grid lg:grid-cols-3 gap-4">
        <InfoCard title="Mission" body={b.mission} />
        <InfoCard title="Vision" body={b.vision} />
        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Values</div>
          <div className="flex flex-wrap gap-2">
            {(b.values || []).map((v, i) => (
              <span key={i} className="px-3 py-1 rounded-full glass text-xs">
                {v}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Color palette */}
      {b.color_palette && (
        <section>
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Color palette</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(b.color_palette).map(([key, c]) => (
              <div key={key} className="glass rounded-2xl overflow-hidden">
                <div className="h-24" style={{ background: c.hex }} />
                <div className="p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">{key}</div>
                  <div className="text-sm font-semibold mt-1">{c.name}</div>
                  <div className="text-xs font-mono text-white/50 mt-1">{c.hex}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Typography */}
      {b.typography && (
        <section className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Type className="h-4 w-4 text-orange-400" />
            <div className="text-xs uppercase tracking-[0.25em] text-white/40">Typography</div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs text-white/50">Heading</div>
              <div className="font-heading text-4xl mt-1">{b.typography.heading_font}</div>
            </div>
            <div>
              <div className="text-xs text-white/50">Body</div>
              <div className="text-2xl mt-1">{b.typography.body_font}</div>
            </div>
          </div>
          {b.typography.rationale && (
            <p className="text-sm text-white/60 mt-4 leading-relaxed">{b.typography.rationale}</p>
          )}
        </section>
      )}

      {/* Brand voice */}
      {b.brand_voice && (
        <section className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="h-4 w-4 text-orange-400" />
            <div className="text-xs uppercase tracking-[0.25em] text-white/40">Brand voice</div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs text-white/50">Personality</div>
              <div className="text-lg font-semibold mt-1">{b.brand_voice.personality}</div>
              <div className="text-xs text-white/50 mt-4">Tone</div>
              <div className="text-sm text-white/80 mt-1">{b.brand_voice.tone_of_voice}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-green-400 mb-2">Do</div>
                <ul className="text-sm text-white/70 space-y-1.5">
                  {(b.brand_voice.do || []).map((x, i) => (
                    <li key={i}>✓ {x}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs text-rose-400 mb-2">Don't</div>
                <ul className="text-sm text-white/70 space-y-1.5">
                  {(b.brand_voice.dont || []).map((x, i) => (
                    <li key={i}>✗ {x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Story + pillars */}
      <div className="grid lg:grid-cols-2 gap-4">
        {b.brand_story && (
          <div className="glass rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Brand story</div>
            <p className="text-white/70 leading-relaxed">{b.brand_story}</p>
          </div>
        )}
        {b.messaging_pillars && (
          <div className="glass rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Messaging pillars</div>
            <ul className="space-y-3">
              {b.messaging_pillars.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="font-heading font-black text-xl brand-gradient-text shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-white/80">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoCard({ title, body }) {
  return (
    <div className="glass rounded-2xl p-6">
      <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">{title}</div>
      <p className="text-white/80 leading-relaxed">{body}</p>
    </div>
  );
}
